import { Stack, Typography } from '@mui/material';
import { permission } from '@nohara-dxdev/oidc-core';
import { LoadingScreen } from '@nohara-dxdev/react-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useTranslation } from 'react-i18next';

import { useFeatureFlagAdminState } from '../hooks/use-feature-flag-admin-state';
import { useUserFeatureFlagAdminState } from '../hooks/use-user-feature-flag-admin-state';

import { FeatureFlagOrgToggle } from './feature-flag-org-toggle';
import { FeatureFlagUserToggle } from './feature-flag-user-toggle';

type Mode = 'org' | 'user';

interface FeatureFlagsListProps {
  mode: Mode;
  userId?: string;
}

const aiAgentPerms = [permission.aiAgent.enable, permission.aiAgent.disable];

export const FeatureFlagsList = ({ mode, userId }: FeatureFlagsListProps) => {
  const { t } = useTranslation();
  const subject = useScopeIds();
  const { orgId } = subject;

  return (
    <OidcProtectedComponent requireAny={aiAgentPerms} subject={subject}>
      <Stack spacing={2}>
        <Typography variant="h6">{t('featureFlags.section.title')}</Typography>
        {mode === 'org' ? (
          <OrgFlagRows orgId={orgId} />
        ) : (
          <UserFlagRows orgId={orgId} userId={userId} />
        )}
      </Stack>
    </OidcProtectedComponent>
  );
};

interface OrgFlagRowsProps {
  orgId: string | null | undefined;
}

const OrgFlagRows = ({ orgId }: OrgFlagRowsProps) => {
  const adminState = useFeatureFlagAdminState(orgId);

  if (adminState.isLoading) return <LoadingScreen />;
  if (!orgId) return null;

  return (
    <>
      {adminState.flags.map((row) => (
        <FeatureFlagOrgToggle key={row.flagKey} flagKey={row.flagKey} orgEnabled={row.orgEnabled} />
      ))}
    </>
  );
};

interface UserFlagRowsProps {
  orgId: string | null | undefined;
  userId: string | undefined;
}

const UserFlagRows = ({ orgId, userId }: UserFlagRowsProps) => {
  const adminState = useUserFeatureFlagAdminState(orgId, userId);

  if (adminState.isLoading) return <LoadingScreen />;
  if (!orgId || !userId) return null;

  return (
    <>
      {adminState.flags.map((row) => (
        <FeatureFlagUserToggle
          key={row.flagKey}
          flagKey={row.flagKey}
          userId={userId}
          orgEnabled={row.orgEnabled}
          userEnabled={row.userEnabled}
          effective={row.effective}
        />
      ))}
    </>
  );
};
